import React, { useEffect, useState } from 'react';
import { Boxes, Percent, Search, BarChart3, ShieldCheck, PlusCircle, QrCode, PlaySquare, Briefcase, Store, MessageSquare, Tags, CalendarDays } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import Loading from './Loading.tsx';
import socioService from '../services/socioService.tsx';
import { useSocioContext } from '../context/socioContext.tsx';
import { Socio } from '../types/Socio.tsx';
import { useNavigate } from 'react-router-dom';
import Scheleton from './Scheleton.tsx';
import { SearchResults } from './buscador/SearchResults.tsx';
import {
  getDeudaPorMes,
  getOcupacionPorTamano,
  getPorcentajeDeuda,
  getResumenCajas,
} from '../services/dashboardService.ts';


export default function AdminDashboard() {
  const {socio, setSocio} = useSocioContext();
  const [listaSocios, setListaSocios] = useState<Socio[] | null>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<{
    resumenCajas?: any;
    ocupacionPorTamano?: any[];
    porcentajeDeuda?: any;
    deudaPorMes?: any[];
  }>({});
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [anioDeuda, setAnioDeuda] = useState<number>(new Date().getFullYear());
  const [periodoFiltro, setPeriodoFiltro] = useState<number | undefined>();

  const handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSearchResults(true);
    setError(null);
    setListaSocios([]);
    if (!searchTerm.trim()) {
      setError('El campo de búsqueda no puede estar vacío.');
      setSearchResults(false);
      return;
    }

    try {
      const data = await socioService.searchSocios(searchTerm, getAccessTokenSilently);
      if (data.length === 0) {
        setError('No se encontraron socios que coincidan con la búsqueda.');
      } else {
        setListaSocios(data);
      }
    } catch (err) {
      setError('No se pudieron cargar los socios. Intenta nuevamente.');
      console.error('Error en la búsqueda:', err);
    } finally {
      setSearchResults(false);
    }
  };


  const handleSelectSocio = async (socioId: string) => {
    try {
      setLoading(true);
      const socioData = await socioService.getSocioById(socioId, getAccessTokenSilently);
      setSocio(socioData);
      socioService.saveSelectedSocioInLocalStorage(socioData);
      navigate('/inicio')
    } catch (err) {
      setError('Error al obtener los datos del socio');
      console.error('Error al seleccionar socio:', err);
    } finally {
      setLoading(false);
    }
    
  };

  const loadDashboard = async (anio: number, periodo?: number) => {
    try {
      setLoadingDashboard(true);
      const [resumen, ocupacion, porcentaje, deudaMes] = await Promise.all([
        getResumenCajas(),
        getOcupacionPorTamano(),
        getPorcentajeDeuda(anio, periodo),
        getDeudaPorMes(anio),
      ]);
      setDashboardData({
        resumenCajas: resumen,
        ocupacionPorTamano: ocupacion,
        porcentajeDeuda: porcentaje,
        deudaPorMes: deudaMes,
      });
    } catch (err) {
      console.error('No se pudo cargar el dashboard', err);
    } finally {
      setLoadingDashboard(false);
    }
  };

  useEffect(() => {
    loadDashboard(anioDeuda);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showSearchModal) {
      setModalVisible(true);
    } else {
      setModalVisible(false);
    }
  }, [showSearchModal]);

  const adminCards = [
    {
      title: 'Buscar socio',
      desc: 'Abrí un buscador rápido con resultados en vivo.',
      badge: 'Socios',
      icon: Search,
      action: () => setShowSearchModal(true),
      accent: 'bg-blue-50 text-blue-600',
    },
    {
      title: 'Crear socio',
      desc: 'Da de alta un nuevo socio y vinculalo por email.',
      badge: 'Socios',
      icon: PlusCircle,
      action: () => navigate('/socios-nuevo'),
      accent: 'bg-indigo-50 text-indigo-600',
    },
    {
      title: 'Gestión de eventos',
      desc: 'Creá, editá y asigná socios a los eventos de la cámara.',
      badge: 'Eventos',
      icon: CalendarDays,
      action: () => navigate('/eventos-admin'),
      accent: 'bg-purple-50 text-purple-600',
    },
    {
      title: 'Gestión de cupones',
      desc: 'Asigná, editá o creá cupones para socios.',
      badge: 'Beneficios',
      icon: Percent,
      action: () => navigate('/cupones'),
      accent: 'bg-emerald-50 text-emerald-600',
      porImplementar: true,
    },
    {
      title: 'Gestión de servicios',
      desc: 'Asigná servicios del catálogo a socios.',
      badge: 'Servicios',
      icon: ShieldCheck,
      action: () => navigate('/servicios-admin'),
      accent: 'bg-blue-50 text-blue-600',
    },
    {
      title: 'Categorías de socios',
      desc: 'Creá categorías y asigná servicios por categoría.',
      badge: 'Socios',
      icon: Tags,
      action: () => navigate('/categorias'),
      accent: 'bg-emerald-50 text-emerald-600',
    },
    {
      title: 'Crear servicio',
      desc: 'Da de alta un nuevo servicio en el catálogo.',
      badge: 'Configuración',
      icon: PlusCircle,
      action: () => navigate('/servicios-nuevo'),
      accent: 'bg-indigo-50 text-indigo-600',
    },
    {
      title: 'Cajas de seguridad',
      desc: 'Administrá cajas, tamaños y asignaciones.',
      badge: 'Infraestructura',
      icon: Boxes,
      action: () => navigate('/cajas'),
      accent: 'bg-indigo-50 text-indigo-600',
    },
    {
      title: 'Puntos por comercio',
      desc: 'Configura comercios, puntos y QR para imprimir.',
      badge: 'Puntos',
      icon: Percent,
      action: () => navigate('/puntos-admin'),
      accent: 'bg-amber-50 text-amber-700',
      porImplementar: true,
    },
    {
      title: 'Comercios del socio (QR)',
      desc: 'Acceso rápido para abrir comercios y generar QR dinámico.',
      badge: 'QR',
      icon: QrCode,
      action: () => navigate('/puntos-admin'),
      accent: 'bg-sky-50 text-sky-700',
    },
    {
      title: 'Cámara Stream (ABM)',
      desc: 'Alta, baja y modificación de videos de capacitaciones.',
      badge: 'Contenido',
      icon: PlaySquare,
      action: () => navigate('/camara-stream-admin'),
      accent: 'bg-cyan-50 text-cyan-700',
      porImplementar: true,
    },
    {
      title: 'Bolsa de trabajo (ABM)',
      desc: 'Publicar y administrar búsquedas laborales.',
      badge: 'Empleo',
      icon: Briefcase,
      action: () => navigate('/bolsa-trabajo-admin'),
      accent: 'bg-violet-50 text-violet-700',
    },
    {
      title: 'Marketplace Admin',
      desc: 'Administrar comercios, productos y emprendedores (alta, baja, aprobación y password).',
      badge: 'Marketplace',
      icon: Store,
      action: () => navigate('/marketplace-admin'),
      accent: 'bg-rose-50 text-rose-700',
      porImplementar: true,
    },
    {
      title: 'WhatsApp Cámara',
      desc: 'Chats, envío y recepción de mensajes de WhatsApp.',
      badge: 'WhatsApp',
      icon: MessageSquare,
      action: () => navigate('/whatsapp-admin'),
      accent: 'bg-green-50 text-green-700',
    },
    {
      title: 'Dashboard de reportes',
      desc: 'KPIs de socios, pagos y contratos.',
      badge: 'Reportes',
      icon: BarChart3,
      action: () => navigate('/dashboard'),
      accent: 'bg-slate-100 text-slate-700',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8 sm:px-6 lg:px-10">
      <div className="mx-auto flex max-w-6xl flex-col gap-6">
      
        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
          <div className="mb-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Backoffice</p>
            <h1 className="text-2xl font-semibold text-slate-900">Panel de Administración</h1>
            <p className="text-sm text-slate-600">Atajos rápidos para administrar socios, cupones y cajas.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {adminCards.map((card) => {
              const Icon = card.icon;
              const esPorImplementar = !!card.porImplementar;
              return (
                <button
                  key={card.title}
                  onClick={esPorImplementar ? undefined : card.action}
                  disabled={esPorImplementar}
                  className={`group flex h-full flex-col justify-between rounded-2xl border border-slate-200 p-4 text-left shadow-sm transition ${
                    esPorImplementar
                      ? 'cursor-not-allowed bg-slate-50/60 opacity-75'
                      : 'bg-slate-50/80 hover:-translate-y-1 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        {card.badge}
                      </p>
                      <p className="text-lg font-semibold text-slate-900">{card.title}</p>
                      <p className="mt-1 text-sm text-slate-600">{card.desc}</p>
                    </div>
                    <div className={`rounded-full p-3 ${card.accent}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                  {esPorImplementar ? (
                    <span className="mt-3 inline-flex w-fit items-center gap-1 self-start rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-800">
                      Por implementar
                    </span>
                  ) : (
                    <div className="mt-3 text-sm font-semibold text-blue-600 opacity-0 transition group-hover:opacity-100">
                      Ir →
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {showSearchModal && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm transition-all duration-200 ${
            modalVisible ? "bg-slate-900/60 opacity-100" : "bg-slate-900/60 opacity-0"
          }`}
        >
          <div
            className={`w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl transition-all duration-200 ease-out transform ${
              modalVisible ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-4"
            }`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Socios</p>
                <h2 className="text-xl font-semibold text-slate-900">Buscar socio</h2>
                <p className="text-sm text-slate-600">
                  Escribí el nombre o ID y seleccioná para abrir su perfil.
                </p>
              </div>
              <button
                onClick={() => setShowSearchModal(false)}
                className="rounded-lg px-3 py-1 text-sm font-semibold text-slate-600 hover:bg-slate-100"
              >
                Cerrar
              </button>
            </div>

            <form onSubmit={handleSearch} className="mt-4 space-y-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o ID"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-36 text-base shadow-inner focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
                >
                  Buscar
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Tip: podés usar nombre completo, parcial o número de socio/DNI.
              </p>
            </form>

            {error && (
              <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            {loading && <div className="mt-3"><Loading /></div>}
            {searchResults && <div className="mt-3"><Scheleton /></div>}

            <div className="mt-4 max-h-72 overflow-y-auto rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200">
              {listaSocios && listaSocios.length > 0 ? (
                <SearchResults
                  listaSocios={listaSocios}
                  handleSelectSocio={(id) => {
                    handleSelectSocio(id);
                    setShowSearchModal(false);
                  }}
                />
              ) : (
                !loading &&
                !searchResults && (
                  <p className="text-sm text-slate-500">
                    Sin resultados aún. Buscá un socio para ver la lista.
                  </p>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DashboardCard = ({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
}) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
      {title}
    </p>
    <p className="text-xl font-semibold text-slate-900">{value}</p>
    {subtitle && <p className="text-xs text-slate-600">{subtitle}</p>}
  </div>
);
